import { inject, Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
    ApiQuery,
    Facet,
    FacetRequest,
    FacetValueConstraint,
    FacetValueOptions,
    ShFacetDataService,
    StructuredQuery
} from '@stylehaven/components';

import { SubmapFacetValue } from '../entities/submap-node.entity';
import { DottedKey } from '../entities/metadata-field.entity';
import { SUBMAP_FACET_VALUE_COUNT } from '../helpers/submap-tree.helper';

/**
 * HFBD-6 decision 2: where the Submap filter's values come from.
 *
 * The filter **cannot read the field it queries.** A bucket on `Dynamic.DocumentPathTags.Tag` reads
 * `1 Voorbereiding (3)` without saying which parent it sat under, so full paths cannot be rebuilt from it.
 * This asks the engine for `Dynamic.DocumentPath` — the whole path — through `Source.CustomFacets`, which
 * facets any dotted key with **no tenant profile entry**. No tenant change is required for the values.
 *
 * ⚠ **Two calls now describe one filter, and that is not redundancy.** The panel keeps requesting the
 * profile's own segment facet, because that is the field the profile lists; the tree draws from this call.
 * They cannot mislead, because the **query** stays on the segment field.
 *
 * **One call builds the whole tree.** An Inhoud tab's query is already scoped to one dossier through
 * `Predefined`, so a single request returns every path in that dossier and expansion costs nothing.
 */
@Injectable({ providedIn: 'root' })
export class SubmapFacetService {
    private readonly facets: ShFacetDataService = inject(ShFacetDataService);

    /**
     * The paths in the current result population, as buckets.
     *
     * `prefix` narrows one level server-side and is only ever sent above the engine's 100-value ceiling —
     * ⚠ where it is the **only** mechanism that reaches a folder at all, because `StartIndex` cannot cross
     * that line either (`startIndex + numberOfValues > 100` fails with `400 EPARAMINV`).
     */
    public getSubmapValues(query: ApiQuery, prefix?: string): Observable<SubmapFacetValue[]> {
        return this.facets.getFacets(this.toRequest(query, prefix)).pipe(map(facets => toSubmapValues(facets)));
    }

    private toRequest(query: ApiQuery, prefix?: string): FacetRequest {
        const options: FacetValueOptions = {
            // The profile's 10 suits a flat list of a handful of values; a folder level is a navigation
            // surface. 100 is the engine's hard ceiling — asking for more fails the whole request.
            DefaultNumberOfValues: SUBMAP_FACET_VALUE_COUNT,
            IncludeMissingValue: false
        };

        const constraint: FacetValueConstraint | undefined = prefix === undefined ? undefined : { Prefix: prefix };

        return {
            Search: { StructuredQuery: this.strippedQuery(query).toApiStructuredQuery() },
            Source: { CustomFacets: [DottedKey.DocumentPath] },
            Value: {
                Options: { [DottedKey.DocumentPath]: options },
                ...(constraint ? { Constraints: { [DottedKey.DocumentPath]: constraint } } : {})
            }
        };
    }

    /**
     * ⚠ The queried facet comes **out** of the query before this call is made.
     *
     * Without it, the call is scoped by the active submap selection and the sibling folders vanish the moment
     * the filter is used — a tree whose siblings disappear when you touch it is worse than the flat list it
     * replaces. The mechanism is the one the *Toon meer* dialog already ships.
     */
    private strippedQuery(query: ApiQuery): ApiQuery {
        const structuredQuery: StructuredQuery = {
            ...query.structuredQuery,
            Facets: (query.structuredQuery.Facets ?? []).filter(
                facet => facet.DottedKey !== DottedKey.DocumentPathTagsTag
            )
        };

        return new ApiQuery(structuredQuery, query.restriction, query.fieldSelect);
    }
}

/**
 * The response, reduced to what the tree needs.
 *
 * ⚠ A `Missing` bucket arrives **unasked** on a `CustomFacets` call — *Geen Bron document*, 21182 records in
 * the live probe, even where `IncludeMissingValue` was never requested. It is dropped here: decision 1 gives
 * a folderless stuk no line, and 21182 is exactly the count that would dominate a level if it became one.
 */
export function toSubmapValues(facets: Facet[]): SubmapFacetValue[] {
    const facet: Facet | undefined = facets.find(candidate => candidate.DottedKey === DottedKey.DocumentPath);

    return (facet?.Values ?? [])
        .filter(value => !value.Missing)
        .map(value => ({ path: value.Value, count: value.Count }));
}
